<template>
    <VueSidePanel
        @closed="closeDrawer"
        v-model="isOpened"
        lock-scroll
        hide-close-btn
        :side="locale == 'ar' ? 'right' : 'left'"
        :no-close="isOpened && loadingOrder"
        z-index="1000"
    >
        <div
            class="detailsContainer d-flex flex-column offcanvas offcanvas-start"
            tabindex="-1"
            id="offcanvasExample"
            aria-labelledby="offcanvasExampleLabel"
            v-if="isOpened && loadingOrder"
        >
            <OrderDetailsSkeleton />
        </div>
        <div
            class="detailsContainer d-flex flex-column offcanvas offcanvas-start"
            tabindex="-1"
            id="offcanvasExample"
            aria-labelledby="offcanvasExampleLabel"
            v-if="isOpened && !loadingOrder"
        >
            <div class="header d-flex justify-content-between flex-row">
                <span>{{ $t("Order Details") }}</span>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    @click="closeDrawer"
                ></button>
            </div>
            <div class="orderInfo d-flex flex-column">
                <div class="d-flex justify-content-between flex-row">
                    <span class="label">{{ $t("Order Number") }}</span>
                    <div
                        class="data"
                        data-toggle="tooltip"
                        data-placement="top"
                        :title="`${order?.order_number ?? '-'}`"
                    >
                        {{ order?.order_number ?? "-" }}
                    </div>
                </div>
                <div
                    v-if="order?.order_status"
                    class="d-flex justify-content-between align-items-center flex-row"
                >
                    <span class="label">{{ $t("Order State") }}</span>
                    <div class="d-flex align-items-center data">
                        <div class="dropdown" v-if="!loadingStatus">
                            <button
                                class="btn btn-sm ripple optionsButton p-0"
                                type="button"
                                id="dropdownMenuButton"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                @click="fetchOrderStatusOptions"
                            >
                                <img
                                    height="20"
                                    width="20"
                                    :src="loadFile('arrowDown.svg')"
                                />
                            </button>
                            <div
                                class="dropdown-menu ripple p-3"
                                aria-labelledby="dropdownMenuButton"
                            >
                                <div v-if="fetchingStatusesLoading">{{ $t('loading') }}</div>
                                <div v-if="!fetchingStatusesLoading && !statusOptions.length">{{ $t('no-options-found') }}</div>
                                <div
                                    :class="[
                                        'orderOptions',
                                        {
                                            firstOption: index == 0,
                                            lastOption:
                                                index ==
                                                statusOptions.length - 1,
                                        },
                                    ]"
                                    v-for="(option, index) in statusOptions"
                                    :key="index"
                                    @click="
                                        () =>
                                            updateOrderStatus(
                                                option.status
                                            )
                                    "
                                >
                                    <span>{{ option.text }}</span>
                                </div>
                            </div>
                        </div>
                        <span
                            v-else
                            class="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                        ></span>
                        <div
                            data-toggle="tooltip"
                            data-placement="top"
                            :title="`${
                                order
                                    ? $t(`order-status-enum.${order?.order_status?.main_order_status}`)
                                    : '-'
                            }`"
                            :class="[
                                'data',
                                {
                                    doneStatus:
                                        order?.order_status
                                            ?.main_order_status ==
                                        OrderStatuses.done,
                                },
                            ]"
                        >
                            {{
                                order
                                    ? $t(`order-status-enum.${order?.order_status?.main_order_status}`)
                                    : "-"
                            }}
                        </div>
                    </div>
                </div>
                <div class="d-flex justify-content-between flex-row">
                    <span class="label">{{ $t("Order Date") }}</span>
                    <div
                        class="data"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        :title="`${orderFormattedDate(order?.created_at)}`"
                    >
                        {{ orderFormattedDate(order?.created_at) }}
                    </div>
                </div>
            </div>
            <div class="itemsContainer" v-if="itemsProps.length > 0">
                <div
                    v-for="(item, index) in itemsProps"
                    :key="`items-${index}`"
                    class="orderItem"
                >
                    <img
                        height="50"
                        width="50"
                        class="align-self-center rounded"
                        :src="
                            item?.image ?? loadFile('logo_white_backgournd.png')
                        "
                    />
                    <div class="d-flex flex-column gap-1">
                        <span class="sku"
                            >{{ $t("sku") }}: {{ item?.sku }}</span
                        >
                        <span class="title">{{
                            item?.title ?? item?.alternativeTitle
                        }}</span>
                        <span class="price">
                            {{ order?.order_total_currency ?? "SAR" }}
                            {{ formatStringWithCommas(item?.price)  }}
                        </span>
                    </div>
                    <div class="quantity ms-auto mt-auto">
                        {{ $t("quantity") }} {{ item?.quantity }}
                    </div>
                </div>
            </div>
            <div class="paymentSummary d-flex flex-column mt-auto">
                <div class="d-flex justify-content-between flex-row">
                    <span class="label">{{ $t("Subtotal") }}</span>
                    <span class="data">
                        {{ order?.order_total_currency ?? "SAR" }}
                        {{
                            formatStringWithCommas(
                                numberTo2DigitsAfterDecimalPoint(
                                    order?.proudct_total_amount
                                )
                            )
                        }}
                    </span>
                </div>
                <div class="d-flex justify-content-between flex-row">
                    <span class="label">{{ $t("Tax") }}</span>
                    <span class="data">
                        {{ order?.order_total_currency ?? "SAR" }}
                        {{
                            formatStringWithCommas(
                                numberTo2DigitsAfterDecimalPoint(
                                    order?.total_tax
                                )
                            )
                        }}
                    </span>
                </div>
                <div class="d-flex justify-content-between flex-row">
                    <span class="label">{{ $t("Shipment") }}</span>
                    <span class="data">
                        {{ order?.order_total_currency ?? "SAR" }}
                        {{
                            formatStringWithCommas(
                                numberTo2DigitsAfterDecimalPoint(
                                    order?.shipment_price
                                )
                            )
                        }}
                    </span>
                </div>
                <div class="d-flex justify-content-between total flex-row">
                    <span class="label">{{ $t("Total") }}</span>
                    <span class="data">
                        {{ order?.order_total_currency ?? "SAR" }}
                        {{
                            formatStringWithCommas(
                                numberTo2DigitsAfterDecimalPoint(
                                    order?.order_total_amount
                                )
                            )
                        }}
                    </span>
                </div>
                <div
                    class="orderShipmentContainer"
                    @click="orderShipment"
                    v-if="
                        paymentActions.status == PaymentStatus.Paid &&
                        order.order_status?.main_order_status ==
                            OrderStatuses.pending
                    "
                >
                    <button
                        :disabled="loading"
                        type="button"
                        class="btn ripple btn-custom"
                    >
                        <span>{{ $t("Order Shipment") }}</span>
                    </button>
                </div>
                <div
                    class="generateLinkButtonContainer"
                    v-if="paymentActions.status == PaymentStatus.Generate"
                >
                    <button
                        @click="generatePaymentLink"
                        :disabled="
                            loading ||
                            order?.order_status?.main_order_status ==
                                OrderStatuses.cancelled
                        "
                        type="button"
                        class="btn ripple btn-custom"
                    >
                        <span
                            v-if="loading"
                            class="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                        ></span>
                        <span v-if="!loading">{{
                            $t("Generate payment link")
                        }}</span>
                    </button>
                </div>
                <div
                    class="withPaymentLink"
                    v-if="
                        [PaymentStatus.Valid, PaymentStatus.Expired].includes(
                            paymentActions.status
                        )
                    "
                >
                    <div>
                        {{ $t("Payment Link") }} &nbsp;
                        <img
                            v-if="paymentActions.status == PaymentStatus.Valid"
                            data-toggle="tooltip"
                            data-placement="top"
                            :title="`${$t('Available until')}${
                                paymentActions.expiryDate
                            }`"
                            :src="loadFile('info.svg')"
                            height="16"
                            width="16"
                        />
                    </div>
                    <div class="input-group paymentActions">
                        <input
                            readonly
                            v-model="paymentActions.link"
                            :class="[
                                {
                                    expired:
                                        paymentActions.status ==
                                        PaymentStatus.Expired,
                                },
                            ]"
                            aria-label="paymentLink"
                            class="form-control"
                        />
                        <div
                            v-if="paymentActions.status == PaymentStatus.Valid"
                            class="input-group-append"
                        >
                            <span
                                @click="copyLink"
                                class="input-group-text ripple"
                            >
                                <img
                                    :src="loadFile('copy.svg')"
                                    height="14"
                                    width="14"
                                />
                            </span>
                            <span
                                @click="sendLink"
                                :class="[
                                    'input-group-text',
                                    'addonSend',
                                    'ripple',
                                    { arBorders: locale == 'en' },
                                    { enBorders: locale == 'ar' },
                                ]"
                            >
                                <img
                                    :src="loadFile('send.svg')"
                                    height="14"
                                    width="14"
                                />
                            </span>
                        </div>
                        <div
                            v-if="
                                paymentActions.status == PaymentStatus.Expired
                            "
                            :class="[
                                'input-group-append',
                                { noPointerEvents: loading },
                            ]"
                        >
                            <span
                                @click="generatePaymentLink"
                                :disabled="loading"
                                :class="[
                                    'input-group-text',
                                    'addonSend',
                                    'ripple',
                                    { arBorders: locale == 'en' },
                                    { enBorders: locale == 'ar' },
                                ]"
                            >
                                <span
                                    v-if="loading"
                                    class="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                <img
                                    v-if="!loading"
                                    :src="loadFile('refresh.png')"
                                    height="14"
                                    width="14"
                                />
                            </span>
                        </div>
                    </div>
                </div>
                <button @click="showOrderInLinker" class="showDetailsButton">
                    {{ $t("Show in Linker") }}
                </button>
            </div>
        </div>
    </VueSidePanel>
</template>

<script setup>
import { ref, computed, watch, reactive } from "vue";
import axios from "axios";
import { useI18n } from "vue-i18n";
import {
    OrderStatuses,
    InvoiceStatus,
    PaymentStatus,
} from "@/components/whatsapp-managment/interfaces.js";
import {
    orderFormattedDate,
    loadFile,
    formatStringWithCommas,
    numberTo2DigitsAfterDecimalPoint,
} from "@/components/utils.js";
import { useStore } from "vuex";
import { Clipboard } from "v-clipboard";
import OrderDetailsSkeleton from './OrderDetailsSkeleton.vue'

const store = useStore();

const { t, locale } = useI18n();

const isOpened = ref(false);
const base_url = window?.location?.origin;
const loading = ref(false);
const loadingOrder = ref(false);
const loadingStatus = ref(false);
const order = ref(null);
const fetchingStatusesLoading = ref(false);

const statusOptions = ref([]);

const paymentActions = reactive({
    link: "",
    status: PaymentStatus.Generate,
    expiryDate: "",
});

const storeOrder = computed(() => store.getters["Orders/selectedDetailsOrder"]);

const fetchOrder = async () => {
    try {
        if (loadingOrder.value) return;
        loadingOrder.value = true;
        isOpened.value = true;
        const { data } = await axios.get(
            `${base_url}/api/v1/orders/${storeOrder.value.id}`
        );
        order.value = data?.result?.orders ?? {};
        getPaymentStatus(order.value);
        loadingOrder.value = false;
    } catch {
        loadingOrder.value = false;
        store.commit(
            "Toasts/addToast",
            {
                message: t("Fetching Order Details Error"),
                type: "error",
            },
            { root: true }
        );
        closeDrawer();
    }
};

const fetchOrderStatusOptions = async () => {
    try {
        if (fetchingStatusesLoading.value) return;
        fetchingStatusesLoading.value = true;
        statusOptions.value = [];
        const id = storeOrder.value.id;
        const { data } = await axios.get(
            `${base_url}/api/main-order/orders/${id}/available-statuses`,
            {
                headers: {
                    locale: locale.value
                }
            }
        );
        const res = data.result?.list || [];
		statusOptions.value = res.map((item) => {
			return {
			    status: item.id,
				text: item.text,
			};
		});
    } finally {
        fetchingStatusesLoading.value = false;
    }
}

const updateOrderStatus = async (newStatus) => {
    if (order.value.order_status.main_order_status == newStatus) return;
    try {
        if (loadingStatus.value) return;
        loadingStatus.value = true;
        await axios.put(
            `${base_url}/api/main-order/orders/${order.value.id}/update-status`,
            {
                order_status: newStatus,
            }
        );
        order.value.order_status.main_order_status = newStatus;
        loadingStatus.value = false;
    } catch {
        loadingStatus.value = false;
        store.commit(
            "Toasts/addToast",
            {
                message: t("Error Msg"),
                type: "error",
            },
            { root: true }
        );
    }
};

watch(
    () => storeOrder,
    async (newVal) => {
        if (newVal.value) {
            await fetchOrder();
        }
    },
    { deep: true }
);

const itemsProps = computed(() => {
    const areItemsVisible =
        Array.isArray(order.value?.main_order_items) &&
        order.value?.main_order_items?.length > 0;
    if (!areItemsVisible) return [];
    return order.value?.main_order_items.map((item) => getItemDetails(item));
});

const getItemDetails = (item) => {
    const itemProps = {
        title: "-",
        alternativeTitle: "",
        sku: "-",
        quantity: 0,
        price: 0,
        image: null,
    };

    const { translations, product_image} = item?.product ?? {};
    itemProps.alternativeTitle = item?.product?.available_name;
    if (Array.isArray(translations) && translations.length > 0) {
        const englishTranslation = translations.find(
            (elem) => elem?.locale == 'en'
        );
        const arabicTranslation = translations.find(
            (elem) => elem?.locale == 'ar'
        );
        itemProps.title = locale.value == 'ar' ?
            arabicTranslation?.title ?? englishTranslation?.title ?? "-" :
            englishTranslation?.title ?? arabicTranslation?.title ?? "-" ;
    }
    itemProps.sku = item?.variant?.vendor_stock_code ?? "-";
    itemProps.quantity = item?.quantity ?? "0";
    itemProps.price = numberTo2DigitsAfterDecimalPoint(item?.unit_price ?? "0");
    const productImage = product_image?.image_thumbnail_url ?? null
    let itemImage = null
    if (Array.isArray(item?.variant?.images)) {
        if (item?.variant?.images.length > 0) {
            itemImage = item?.variant?.images[0]?.path ?? null;
        }
    }
    itemProps.image = itemImage ?? productImage ?? null
    return itemProps;
};

const showOrderInLinker = () => {
    window.open(
        `${base_url}/linked_marketplace/${order.value?.linked_marketplace_id}/orders/show/${order.value?.id}`,
        "_blank"
    );
};

const closeDrawer = () => {
    store.commit("Orders/setSelectedDetailsOrder", null);
    isOpened.value = false;
};

const getPaymentStatus = (order) => {
    const { invoices } = order;
    if (!invoices || (invoices && !invoices.length)) {
        paymentActions.link = "";
        paymentActions.status = PaymentStatus.Generate;
        paymentActions.expiryDate = "";
        return;
    }
    const currentInvoice = invoices[0];
    const currentDate = new Date();
    const expiryDate = new Date(currentInvoice?.expiry_date);
    const formattedExpiryDate = orderFormattedDate(expiryDate, true);
    paymentActions.expiryDate = formattedExpiryDate;

    if (currentInvoice?.invoice_status == InvoiceStatus.success) {
        paymentActions.link = "";
        paymentActions.status = PaymentStatus.Paid;
    }
    if (currentInvoice?.invoice_status == InvoiceStatus.pending) {
        if (currentDate.getTime() > expiryDate.getTime()) {
            paymentActions.link = t("Expired Link");
            paymentActions.status = PaymentStatus.Expired;
        } else {
            paymentActions.link = currentInvoice.invoice_url;
            paymentActions.status = PaymentStatus.Valid;
        }
    }
};

const generatePaymentLink = async () => {
    if (loading.value) return;
    try {
        loading.value = true;
        const response = await axios.get(
            `${base_url}/api/main-order/payment/generate-link/${order.value.id}`
        );

        if (response?.data?.success) {
            // Prepare back up expiry date 2 days from now in case the resposnse doesn't have expiry date
            const now = new Date();
            const backUpExpiryDate = new Date(
                now.getTime() + 2 * 24 * 60 * 60 * 1000
            );
            const year = backUpExpiryDate.getFullYear();
            const month = backUpExpiryDate.getMonth() + 1;
            const day = backUpExpiryDate.getDate();
            const hours = backUpExpiryDate.getHours();
            const minutes = backUpExpiryDate.getMinutes();
            const seconds = backUpExpiryDate.getSeconds();

            const formattedBackUpExpiryDateDate = `${year}-${month
                .toString()
                .padStart(2, "0")}-${day.toString().padStart(2, "0")}T${hours
                .toString()
                .padStart(2, "0")}:${minutes
                .toString()
                .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

            const expiryDate =
                response?.data?.expiry_date ?? formattedBackUpExpiryDateDate;
            // update order with new invoice
            order.value.invoices = [
                {
                    invoice_url: response.data.link,
                    invoice_status: InvoiceStatus.pending,
                    expiry_date: expiryDate,
                },
            ];
            getPaymentStatus(order.value);
        } else {
            swal.fire({
                title: response?.data?.msg ?? $t("Error Msg"),
                allowOutsideClick: false,
                confirmButtonText: t("ok"),
            });
        }
        loading.value = false;
    } catch (error) {
        loading.value = false;
        console.error("Error generating payment link:", error);
    }
};

const orderShipment = () => {
    window.open(
        `${base_url}/orders/index?filter[order_number]=${order.value.order_number}`,
        "_blank"
    );
};

const copyLink = () => {
    Clipboard.copy(order.value?.invoices[0]?.invoice_url);
};

const sendLink = () => {
    store.commit("Chat/setChatMessage", {
        chatMessage: order.value.invoices[0].invoice_url,
    });
};
</script>
<style scoped lang="scss">
@import "../styles/orderDetails.scss";
</style>
